import React, { useMemo, useRef, useState } from 'react';
import { Feather, BookOpen, Download, Share2, Sprout, ArrowRight } from 'lucide-react';
import html2canvas from 'html2canvas';
import { gerarCarta } from '../utils/cartaEngine';

// A Carta da Sua Estação — o centro emocional do resultado.
// Uso:  <CartaDaEstacao result={result} nome={collectName} />
const CartaDaEstacao = ({ result, nome }) => {
  const carta = useMemo(() => gerarCarta(result, { nome }), [result, nome]);
  const ref = useRef(null);
  const [baixando, setBaixando] = useState(false);
  if (!carta) return null;

  const seloCor = { alta: 'text-emerald-300 border-emerald-400/30 bg-emerald-500/10',
                    media: 'text-amber-300 border-amber-400/30 bg-amber-500/10',
                    baixa: 'text-rose-300 border-rose-400/30 bg-rose-500/10' };

  const baixar = async () => {
    if (!ref.current) return;
    setBaixando(true);
    try {
      const canvas = await html2canvas(ref.current, { backgroundColor: '#1b1530', scale: 2, useCORS: true, logging: false });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = 'carta-da-minha-estacao.png';
      a.click();
    } catch (e) { /* silencioso */ } finally { setBaixando(false); }
  };

  const compartilhar = async () => {
    const txt = `Recebi a Carta da minha Estação no Teste das Estações do Coração.\n\n“${carta.semente}”\n\nDescubra a sua:`;
    const url = 'https://estacoes.ocoracaodetodasascoisas.com';
    try {
      if (navigator.share) await navigator.share({ title: 'A Carta da Minha Estação', text: txt, url });
      else { await navigator.clipboard.writeText(`${txt} ${url}`); alert('Carta copiada!'); }
    } catch (e) { console.log('Compartilhamento não disponível', e); }
  };

  return (
    <div className="my-10" data-testid="carta-da-estacao">
      <div className="flex justify-center mb-6">
        <div className="bg-gradient-to-r from-amber-900/40 via-rose-900/40 to-amber-900/40 px-7 py-3.5 rounded-full border border-amber-500/20">
          <div className="flex items-center gap-3">
            <Feather className="w-6 h-6 text-amber-300" />
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>A Carta da Sua Estação</h2>
              <p className="text-white/50 text-xs mt-0.5">Escrita sob medida, a partir do seu coração</p>
            </div>
          </div>
        </div>
      </div>

      <div ref={ref} className="relative rounded-3xl p-7 md:p-10 border border-amber-500/20 overflow-hidden"
           style={{ background: 'linear-gradient(160deg, rgba(69,26,3,0.55), rgba(49,16,40,0.5) 55%, rgba(27,21,48,0.6))' }}>
        {/* selo de confiança */}
        {carta.selo && (
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs mb-6 ${seloCor[carta.selo.flag]}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            Leitura confiável: {carta.selo.flag} · {carta.selo.score}
          </div>
        )}

        <p className="text-amber-100/90 text-xl md:text-2xl mb-6" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
          {carta.saudacao}
        </p>

        <div className="space-y-5" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {carta.blocos.map((b, i) => (
            <div key={i}>
              {b.titulo && <p className="text-amber-300/70 text-xs font-semibold uppercase tracking-widest mb-1.5">{b.titulo}</p>}
              <p className="text-white/85 leading-relaxed text-[1.02rem]">{b.texto}</p>
            </div>
          ))}
        </div>

        {/* caminho pelo livro */}
        {carta.capitulos?.length > 0 && (
          <div className="mt-7 bg-white/5 rounded-2xl p-5 border border-amber-500/15">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-4 h-4 text-amber-300" />
              <p className="text-amber-200 text-sm font-semibold uppercase tracking-wider">Para viver isto através do livro</p>
            </div>
            <div className="space-y-3">
              {carta.capitulos.map((c, i) => (
                <div key={i} className="border-l-2 border-amber-500/30 pl-4">
                  <p className="text-white font-semibold text-sm">{c.capitulo}</p>
                  <p className="text-white/60 text-sm italic leading-relaxed">{c.promessa}</p>
                  <p className="text-amber-300/60 text-xs mt-0.5">{c.porque}</p>
                </div>
              ))}
            </div>
            <a href="https://ocoracaodetodasascoisas.com" target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 rounded-lg transition-all text-sm border border-amber-500/20">
              <BookOpen className="w-4 h-4" /> Conhecer o livro <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        )}

        {/* prática */}
        {carta.pratica && (
          <div className="mt-5 bg-white/5 rounded-2xl p-5 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <Sprout className="w-4 h-4 text-emerald-300" />
              <p className="text-emerald-200 text-sm font-semibold">Uma prática para esta estação · {carta.pratica.titulo}</p>
            </div>
            <p className="text-white/80 leading-relaxed text-sm" style={{ fontFamily: "'Lora', Georgia, serif" }}>{carta.pratica.texto}</p>
          </div>
        )}

        {carta.fecho && (
          <p className="mt-7 text-amber-100/90 leading-relaxed text-lg" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
            {carta.fecho}
          </p>
        )}

        {/* a semente */}
        <div className="mt-7 text-center">
          <p className="text-amber-300/60 text-xs uppercase tracking-widest mb-2">A semente que você leva</p>
          <p className="text-white text-xl md:text-2xl leading-snug" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
            “{carta.semente}”
          </p>
        </div>

        <p className="mt-7 text-right text-white/70 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>{carta.assinatura}</p>

        {carta.posfacio && (
          <p className="mt-6 pt-4 border-t border-white/10 text-white/50 text-sm italic leading-relaxed">{carta.posfacio}</p>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-6">
        <button onClick={baixar} disabled={baixando} data-testid="baixar-carta"
          className="flex items-center gap-2.5 text-white border-none rounded-full px-8 py-4 text-base font-bold transition-all hover:scale-105"
          style={{ background: baixando ? 'rgba(245,158,11,0.4)' : 'linear-gradient(135deg, #F59E0B, #EC4899)', boxShadow: '0 4px 24px rgba(245,158,11,0.3)', cursor: baixando ? 'not-allowed' : 'pointer' }}>
          <Download className="w-5 h-5" /> {baixando ? 'Gerando…' : 'Baixar minha carta'}
        </button>
        <button onClick={compartilhar}
          className="flex items-center gap-2.5 text-white/90 border border-white/20 rounded-full px-8 py-4 text-base font-bold transition-all hover:bg-white/10 hover:scale-105" style={{ cursor: 'pointer' }}>
          <Share2 className="w-5 h-5" /> Compartilhar
        </button>
      </div>
    </div>
  );
};

export default CartaDaEstacao;
